/**
 * CalibrationModal — V14.62 Sprint 19 (Lab).
 *
 * Petit dialog frontal qui s'ouvre après le 2ᵉ clic de l'outil Règle.
 * L'utilisateur saisit la distance réelle (m) entre les 2 points → on
 * calcule scale_m_per_px = meters / pixelDistance et on l'écrit dans le store.
 *
 * Pas de Dialog Radix → un overlay custom léger (le lab doit rester self-contained).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Ruler, X } from 'lucide-react';
import { toast } from 'sonner';
import { useGeometryStore } from './useGeometryStore';

const CalibrationModal = () => {
  const pending = useGeometryStore((s) => s.calibrationPending);
  const commitCalibration = useGeometryStore((s) => s.commitCalibration);
  const cancelCalibration = useGeometryStore((s) => s.cancelCalibration);
  const [value, setValue] = useState('');
  const inputRef = useRef(null);

  const pixelDistance = useMemo(() => {
    if (!pending) return 0;
    return Math.hypot(pending.x2 - pending.x1, pending.y2 - pending.y1);
  }, [pending]);

  useEffect(() => {
    if (pending) {
      setValue('');
      // Auto-focus once the modal mounts (next tick so the input exists).
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [pending]);

  if (!pending) return null;

  const submit = () => {
    const meters = parseFloat(value.replace(',', '.'));
    if (!Number.isFinite(meters) || meters <= 0) {
      toast.error('Saisissez une distance > 0 en mètres');
      return;
    }
    commitCalibration(meters);
    toast.success(`Échelle calibrée : 1 m = ${(pixelDistance / meters).toFixed(1)} px`);
  };

  const onKey = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); submit(); }
    if (e.key === 'Escape') { e.preventDefault(); cancelCalibration(); }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center backdrop-blur-sm"
      style={{ background: 'rgba(2,6,12,0.7)' }}
      data-testid="calibration-modal"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) cancelCalibration(); }}
    >
      <div
        className="w-[420px] max-w-[90vw] rounded-2xl border overflow-hidden shadow-2xl"
        style={{ background: 'rgba(8,12,20,0.96)', borderColor: 'rgba(16,185,129,0.4)' }}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b"
             style={{ borderColor: 'rgba(16,185,129,0.25)' }}>
          <Ruler className="w-4 h-4 text-emerald-400" />
          <h3 className="flex-1 text-sm font-semibold text-white">
            Calibration métrique
          </h3>
          <button type="button"
                  onClick={cancelCalibration}
                  className="w-7 h-7 inline-flex items-center justify-center rounded text-gray-400 hover:text-white hover:bg-white/5"
                  data-testid="calibration-modal-close"
                  title="Annuler (Échap)">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-4 py-4 space-y-4">
          <p className="text-[12px] text-gray-300 leading-relaxed">
            Vous avez tracé une référence de{' '}
            <strong className="text-emerald-300 font-mono">{pixelDistance.toFixed(1)} px</strong>{' '}
            sur le canvas. Indiquez la distance réelle correspondante (en mètres) —
            elle servira à extruder les murs à la bonne hauteur et à dimensionner
            les ouvertures.
          </p>

          <label className="block">
            <span className="text-[10.5px] uppercase tracking-[0.2em] text-emerald-300/80">
              Distance réelle (mètres)
            </span>
            <input
              ref={inputRef}
              type="text"
              inputMode="decimal"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={onKey}
              placeholder="Ex. 5,40"
              className="mt-1 w-full px-3 py-2 rounded border bg-black/40 text-white text-base font-mono focus:outline-none focus:border-emerald-400"
              style={{ borderColor: 'rgba(255,255,255,0.15)' }}
              data-testid="calibration-modal-input"
            />
          </label>

          {value && parseFloat(value.replace(',', '.')) > 0 && (
            <div className="text-[10.5px] text-emerald-300/80 font-mono">
              → Échelle :{' '}
              {(pixelDistance / parseFloat(value.replace(',', '.'))).toFixed(2)} px / m
              <span className="text-gray-500">  ·  </span>
              1 px = {(parseFloat(value.replace(',', '.')) / pixelDistance * 1000).toFixed(2)} mm
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 px-4 py-3 border-t bg-black/30"
             style={{ borderColor: 'rgba(16,185,129,0.18)' }}>
          <button type="button"
                  onClick={cancelCalibration}
                  className="px-3 py-1.5 text-[11px] rounded border border-white/15 text-gray-300 hover:bg-white/5"
                  data-testid="calibration-modal-cancel">
            Annuler
          </button>
          <div className="flex-1" />
          <button type="button"
                  onClick={submit}
                  className="px-4 py-1.5 text-[11px] rounded bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
                  data-testid="calibration-modal-submit">
            Valider l&apos;échelle ↵
          </button>
        </div>
      </div>
    </div>
  );
};

export default CalibrationModal;
